import { useEffect, useRef, useState } from 'react';
import { Check, Plus } from 'lucide-react';

import ActionButton from '../../../components/Buttons/ActionButton';
import InputField from '../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import { RestaurantMenuCategoryDTO } from '../../../types/restaurant/RestaurantMenuTypes';

interface Props {
  label?: string;
  categories: RestaurantMenuCategoryDTO[];
  value: number | '';
  error?: string;
  disabled?: boolean;
  onChange: (categoryId: number) => void;
  onCreateCategory: (
    name: string
  ) => Promise<RestaurantMenuCategoryDTO | null>;
}

/**
 * Selector de categoría de menú con alta "＋ nueva categoría" en línea (input +
 * Agregar). Si no hay categorías, arranca en modo alta. El código de la
 * categoría lo genera el backend a partir del nombre.
 */
const MenuCategoryQuickField = ({
  label = 'Categoría del menú',
  categories,
  value,
  error,
  disabled,
  onChange,
  onCreateCategory,
}: Props) => {
  const [showNew, setShowNew] = useState(categories.length === 0);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const initialised = useRef(false);

  // La primera vez que llegan categorías, si hay alguna, salir del modo alta.
  useEffect(() => {
    if (!initialised.current && categories.length > 0) {
      setShowNew(false);
      initialised.current = true;
    }
  }, [categories.length]);

  const addCategory = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const created = await onCreateCategory(name);
      if (created) {
        onChange(created.id);
        setShowNew(false);
        setNewName('');
      }
    } finally {
      setCreating(false);
    }
  };

  if (showNew) {
    return (
      <div className="space-y-2 rounded-lg border border-primary/20 bg-primary/5 p-3 dark:border-primary/25 dark:bg-primary/10">
        <InputField
          label={`${label} (nueva)`}
          name="menuCategoryNew"
          type="text"
          iconKey="categories"
          value={newName}
          placeholder="Ej. Entradas"
          disabled={disabled || creating}
          error={error}
          onChange={(event) => setNewName(event.target.value)}
        />
        <div className="flex items-center gap-2">
          <ActionButton
            label="Agregar"
            icon={<Check className="h-4 w-4" />}
            variant="primary"
            size="sm"
            fullWidth={false}
            loading={creating}
            disabled={!newName.trim() || creating || disabled}
            onClick={addCategory}
          />
          {categories.length > 0 && (
            <button
              type="button"
              className="text-xs text-bodydark2 hover:underline"
              onClick={() => setShowNew(false)}
            >
              Usar una existente
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <SimpleSearchSelect
        label={label}
        name="menuCategoryId"
        selectedValue={value || null}
        iconKey="categories"
        placeholder="Seleccione una categoría"
        searchPlaceholder="Buscar categoría..."
        required
        disabled={disabled}
        error={error}
        options={categories.map((category) => ({
          id: category.id,
          name: category.name,
        }))}
        onSelect={(selected) => onChange(selected.id)}
      />
      <button
        type="button"
        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        onClick={() => setShowNew(true)}
      >
        <Plus className="h-3.5 w-3.5" />
        Nueva categoría
      </button>
    </div>
  );
};

export default MenuCategoryQuickField;
